import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import {
  ENVIRONMENT,
  TwnVerificationRequest,
  TwnVerificationResponse,
  TwnVerificationResult,
  TwnVerificationStatus,
  TwnVerificationMethod,
  TwnEmploymentRecord,
  TwnFeatureConfig,
  EmploymentVerificationChoice,
} from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';

import { MlaAuthService } from './mla-auth.service';
import { LoggingService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class TwnVerificationService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly authService = inject(MlaAuthService);
  private readonly loggingService = inject(LoggingService);

  readonly verificationStatus = signal<TwnVerificationStatus>('NotStarted');

  readonly isVerifying = signal(false);

  readonly lastResult = signal<TwnVerificationResult | null>(null);

  readonly employmentRecords = signal<TwnEmploymentRecord[]>([]);

  readonly verificationChoice = signal<TwnVerificationMethod | null>(null);

  readonly featureConfig = signal<TwnFeatureConfig>({
    voieEnabled: true,
    twnEnabled: true,
    autoVerifyFirst: true,
  });

  private get mlaApiUrl(): string {
    return this.env.appSettings.mlaApiUrl || '';
  }

  isTwnEnabled(): boolean {
    const config = this.featureConfig();
    return config.twnEnabled && config.voieEnabled;
  }

  shouldAutoVerifyFirst(): boolean {
    const config = this.featureConfig();
    return config.autoVerifyFirst && this.isTwnEnabled();
  }

  async loadFeatureConfig(loanId: string): Promise<TwnFeatureConfig> {
    const url = `${this.mlaApiUrl}api/v1/loan/${loanId}/feature-flags`;

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: Record<string, boolean> }>(url)
      );

      if (response?.success && response.data) {
        const config: TwnFeatureConfig = {
          voieEnabled: response.data['VOIEEnabled'] !== false,
          twnEnabled: response.data['TWNEnabled'] !== false,
          autoVerifyFirst: response.data['AutoVerifyFirst'] !== false,
        };
        this.featureConfig.set(config);
        return config;
      }
    } catch (error) {
      this.loggingService.logException(error, '[TwnVerificationService] Error loading feature config:');
    }

    return this.featureConfig();
  }

  setVerificationChoice(method: TwnVerificationMethod, borrowerId: string): void {
    this.verificationChoice.set(method);

    const choice: EmploymentVerificationChoice = {
      method,
      borrowerId,
      timestamp: new Date().toISOString(),
    };

    sessionStorage.setItem(`twn_choice_${borrowerId}`, JSON.stringify(choice));
  }

  getStoredChoice(borrowerId: string): EmploymentVerificationChoice | null {
    const stored = sessionStorage.getItem(`twn_choice_${borrowerId}`);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  }

  async verifyEmployment(request: TwnVerificationRequest): Promise<TwnVerificationResult> {
    this.isVerifying.set(true);
    this.verificationStatus.set('InProgress');

    const url = `${this.mlaApiUrl}api/v1/employment/verify-twn`;

    try {
      const response = await firstValueFrom(
        this.http.post<TwnVerificationResponse>(url, request)
      );

      if (response?.success && response.data) {
        const result = response.data;
        this.lastResult.set(result);
        this.verificationStatus.set(result.status);

        if (result.employmentRecords?.length) {
          this.employmentRecords.set(result.employmentRecords);
        }

        return result;
      }

      const errorResult: TwnVerificationResult = {
        verificationId: '',
        status: 'Failed',
        errorMessage: response?.errors?.join(', ') || 'Verification failed',
      };
      this.lastResult.set(errorResult);
      this.verificationStatus.set('Failed');
      return errorResult;
    } catch (error) {
      this.loggingService.logException(error, '[TwnVerificationService] Error verifying employment:');

      const errorResult: TwnVerificationResult = {
        verificationId: '',
        status: 'Failed',
        errorMessage: 'Unable to verify employment at this time',
      };
      this.lastResult.set(errorResult);
      this.verificationStatus.set('Failed');
      return errorResult;
    } finally {
      this.isVerifying.set(false);
    }
  }

  async getVerificationStatus(borrowerId: string, loanId: string): Promise<TwnVerificationStatus> {
    const url = `${this.mlaApiUrl}api/v1/employment/twn-status`;
    const params = { borrowerId, loanId };

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: { status: TwnVerificationStatus } }>(url, { params })
      );

      if (response?.success && response.data) {
        this.verificationStatus.set(response.data.status);
        return response.data.status;
      }
    } catch (error) {
      this.loggingService.logException(error, '[TwnVerificationService] Error getting verification status:');
    }

    return 'NotStarted';
  }

  async getEmploymentRecords(borrowerId: string, loanId: string): Promise<TwnEmploymentRecord[]> {
    const url = `${this.mlaApiUrl}api/v1/employment/twn-records`;
    const params = { borrowerId, loanId };

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: TwnEmploymentRecord[] }>(url, { params })
      );

      if (response?.success && response.data) {
        this.employmentRecords.set(response.data);
        return response.data;
      }
    } catch (error) {
      this.loggingService.logException(error, '[TwnVerificationService] Error getting employment records:');
    }

    return [];
  }

  checkTwnGuard(borrowerId: string, manualEntryRoute: string): { shouldRedirect: boolean; route: string } {
    if (!this.isTwnEnabled()) {
      return { shouldRedirect: true, route: manualEntryRoute };
    }

    const choice = this.getStoredChoice(borrowerId);
    if (choice?.method === 'manual') {
      return { shouldRedirect: true, route: manualEntryRoute };
    }

    return { shouldRedirect: false, route: '' };
  }

  isSelectionMade(): boolean {
    return this.verificationChoice() !== null;
  }

  reset(): void {
    this.verificationStatus.set('NotStarted');
    this.isVerifying.set(false);
    this.lastResult.set(null);
    this.employmentRecords.set([]);
    this.verificationChoice.set(null);
  }

  clearStoredChoice(borrowerId: string): void {
    sessionStorage.removeItem(`twn_choice_${borrowerId}`);
  }
}
